import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  MessageSquare, Calendar, CheckCircle, Building2, BarChart3, Shield,
  Download, Star, ChevronDown, ChevronUp, ArrowRight, Smartphone,
  Check, X, Menu, X as XIcon
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const iconMap = {
  MessageSquare, Calendar, CheckCircle, Building2, BarChart3, Shield,
  Download, Star, Smartphone, Check
};

const LandingPage = () => {
  const navigate = useNavigate();
  const { currentUser, loading: authLoading } = useAuth();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openFaq, setOpenFaq] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    // Redirect logged in users to dashboard
    if (!authLoading && currentUser) {
      navigate('/dashboard', { replace: true });
      return;
    }

    // Load landing config
    const loadConfig = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/landing/config`);
        setConfig(response.data);
        
        // Track visit
        axios.post(`${API_URL}/api/landing/track/visit`).catch(() => {});
        
        // Initialize Facebook Pixel if configured
        if (response.data.facebookPixelId) {
          initFacebookPixel(response.data.facebookPixelId);
        }
      } catch (error) {
        console.error('Error loading landing config:', error);
      } finally {
        setLoading(false);
      }
    };

    loadConfig();
  }, [currentUser, authLoading, navigate]);

  const initFacebookPixel = (pixelId) => {
    if (window.fbq) return;
    
    const script = document.createElement('script');
    script.innerHTML = `
      !function(f,b,e,v,n,t,s)
      {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
      n.callMethod.apply(n,arguments):n.queue.push(arguments)};
      if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
      n.queue=[];t=b.createElement(e);t.async=!0;
      t.src=v;s=b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t,s)}(window, document,'script',
      'https://connect.facebook.net/en_US/fbevents.js');
      fbq('init', '${pixelId}');
      fbq('track', 'PageView');
    `;
    document.head.appendChild(script);
  };

  const handleDownloadAPK = async () => {
    if (config?.apkUrl) {
      // Track download
      axios.post(`${API_URL}/api/landing/track/download`).catch(() => {});
      
      // Facebook Pixel event
      if (window.fbq) {
        window.fbq('track', 'Download');
      }
      
      window.open(config.apkUrl, '_blank');
    }
  };

  const handleSignup = () => {
    // Facebook Pixel event
    if (window.fbq) {
      window.fbq('track', 'Lead');
    }
    navigate('/register');
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-green-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!config) return null;

  const { sections } = config;

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm z-50 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <img src="/logo.png" alt="NotiMax" className="w-10 h-10 rounded-xl" />
              <span className="text-xl font-bold text-gray-900">NotiMax</span>
            </div>
            
            {/* Desktop Menu */}
            <div className="hidden md:flex items-center space-x-8">
              {sections.features?.enabled && <a href="#features" className="text-gray-600 hover:text-blue-600 transition">Características</a>}
              {sections.pricing?.enabled && <a href="#pricing" className="text-gray-600 hover:text-blue-600 transition">Precios</a>}
              {sections.faq?.enabled && <a href="#faq" className="text-gray-600 hover:text-blue-600 transition">FAQ</a>}
              <Button variant="outline" onClick={() => navigate('/login')}>Iniciar Sesión</Button>
              <Button onClick={handleSignup} className="bg-gradient-to-r from-blue-500 to-green-500 hover:from-blue-600 hover:to-green-600">
                Prueba Gratis
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <XIcon className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 py-4 px-4 space-y-4">
            {sections.features?.enabled && <a href="#features" className="block text-gray-600" onClick={() => setMobileMenuOpen(false)}>Características</a>}
            {sections.pricing?.enabled && <a href="#pricing" className="block text-gray-600" onClick={() => setMobileMenuOpen(false)}>Precios</a>}
            {sections.faq?.enabled && <a href="#faq" className="block text-gray-600" onClick={() => setMobileMenuOpen(false)}>FAQ</a>}
            <Button variant="outline" className="w-full" onClick={() => navigate('/login')}>Iniciar Sesión</Button>
            <Button onClick={handleSignup} className="w-full bg-gradient-to-r from-blue-500 to-green-500">Prueba Gratis</Button>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      {sections.hero?.enabled && (
        <section className="pt-24 pb-16 md:pt-32 md:pb-24 bg-gradient-to-br from-blue-50 via-white to-green-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-4xl mx-auto">
              <Badge className="mb-4 bg-blue-100 text-blue-700 hover:bg-blue-100">✨ 7 días de prueba gratis</Badge>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                {sections.hero.title}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-green-500"> por WhatsApp</span>
              </h1>
              <p className="text-lg md:text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
                {sections.hero.description}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  size="lg" 
                  onClick={handleSignup}
                  className="bg-gradient-to-r from-blue-500 to-green-500 hover:from-blue-600 hover:to-green-600 text-lg px-8 py-6 rounded-xl shadow-lg hover:shadow-xl transition-all"
                >
                  {sections.hero.ctaPrimary} <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                {config.apkUrl && (
                  <Button 
                    size="lg" 
                    variant="outline"
                    onClick={handleDownloadAPK}
                    className="text-lg px-8 py-6 rounded-xl border-2"
                  >
                    <Download className="mr-2 w-5 h-5" /> Descargar APK
                  </Button>
                )}
              </div>
              <p className="mt-4 text-sm text-gray-500">Sin tarjeta de crédito • Cancela cuando quieras</p>
            </div>
          </div>
        </section>
      )}

      {/* Features Section */}
      {sections.features?.enabled && (
        <section id="features" className="py-16 md:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">{sections.features.title}</h2>
              <p className="text-lg text-gray-600">{sections.features.subtitle}</p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {sections.features.items?.map((feature, index) => {
                const IconComponent = iconMap[feature.icon] || CheckCircle;
                return (
                  <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                    <CardContent className="p-6">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-green-500 rounded-xl flex items-center justify-center mb-4">
                        <IconComponent className="w-6 h-6 text-white" />
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">{feature.title}</h3>
                      <p className="text-gray-600">{feature.description}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* How It Works Section */}
      {sections.howItWorks?.enabled && (
        <section className="py-16 md:py-24 bg-gradient-to-br from-gray-50 to-blue-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">{sections.howItWorks.title}</h2>
              <p className="text-lg text-gray-600">{sections.howItWorks.subtitle}</p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              {sections.howItWorks.steps?.map((step, index) => (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-green-500 rounded-full flex items-center justify-center mx-auto mb-4 text-white text-2xl font-bold shadow-lg">
                    {step.number}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-gray-600">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Pricing Section */}
      {sections.pricing?.enabled && (
        <section id="pricing" className="py-16 md:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">{sections.pricing.title}</h2>
              <p className="text-lg text-gray-600">{sections.pricing.subtitle}</p>
            </div>
            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {/* Monthly Plan */}
              <Card className="border-2 border-gray-200 hover:border-blue-300 transition-all duration-300 hover:shadow-xl">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Plan Mensual</h3>
                  <div className="mb-6">
                    <span className="text-5xl font-bold text-gray-900">${sections.pricing.monthly?.price}</span>
                    <span className="text-gray-600"> {sections.pricing.monthly?.currency}/{sections.pricing.monthly?.period}</span>
                  </div>
                  <ul className="space-y-3 mb-8">
                    {sections.pricing.monthly?.features?.map((feature, index) => (
                      <li key={index} className="flex items-center text-gray-600">
                        <Check className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <Button onClick={handleSignup} className="w-full" variant="outline" size="lg">
                    Comenzar Ahora
                  </Button>
                </CardContent>
              </Card>

              {/* Annual Plan */}
              <Card className="border-2 border-green-400 shadow-xl relative overflow-hidden">
                <div className="absolute top-0 right-0 bg-gradient-to-r from-green-400 to-green-500 text-white px-4 py-1 text-sm font-semibold">
                  {sections.pricing.annual?.savings}
                </div>
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Plan Anual</h3>
                  <div className="mb-6">
                    <span className="text-5xl font-bold text-gray-900">${sections.pricing.annual?.price}</span>
                    <span className="text-gray-600"> {sections.pricing.annual?.currency}/{sections.pricing.annual?.period}</span>
                  </div>
                  <ul className="space-y-3 mb-8">
                    {sections.pricing.annual?.features?.map((feature, index) => (
                      <li key={index} className="flex items-center text-gray-600">
                        <Check className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <Button onClick={handleSignup} className="w-full bg-gradient-to-r from-blue-500 to-green-500 hover:from-blue-600 hover:to-green-600" size="lg">
                    Comenzar Ahora
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>
      )}

      {/* Download Section */}
      {sections.download?.enabled && config.apkUrl && (
        <section id="download" className="py-16 md:py-24 bg-gradient-to-br from-blue-600 to-green-500">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <Smartphone className="w-16 h-16 text-white/90 mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">{sections.download.title}</h2>
            <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">{sections.download.subtitle}</p>
            <Button 
              size="lg" 
              onClick={handleDownloadAPK}
              className="bg-white text-blue-600 hover:bg-gray-100 text-lg px-8 py-6 rounded-xl shadow-lg"
            >
              <Download className="mr-2 w-5 h-5" /> Descargar APK v{config.apkVersion}
            </Button>
            <p className="mt-4 text-white/70 text-sm">{sections.download.description}</p>
          </div>
        </section>
      )}

      {/* Testimonials Section */}
      {sections.testimonials?.enabled && sections.testimonials.items?.length > 0 && (
        <section className="py-16 md:py-24 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">{sections.testimonials.title}</h2>
              <p className="text-lg text-gray-600">{sections.testimonials.subtitle}</p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              {sections.testimonials.items?.map((testimonial, index) => (
                <Card key={index} className="border-0 shadow-lg">
                  <CardContent className="p-6">
                    <div className="flex items-center mb-4">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                      ))}
                    </div>
                    <p className="text-gray-600 mb-4 italic">"{testimonial.text}"</p>
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-green-500 rounded-full flex items-center justify-center text-white font-bold">
                        {testimonial.name.charAt(0)}
                      </div>
                      <div className="ml-3">
                        <p className="font-semibold text-gray-900">{testimonial.name}</p>
                        <p className="text-sm text-gray-500">{testimonial.role}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ Section */}
      {sections.faq?.enabled && (
        <section id="faq" className="py-16 md:py-24 bg-white">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">{sections.faq.title}</h2>
              <p className="text-lg text-gray-600">{sections.faq.subtitle}</p>
            </div>
            <div className="space-y-4">
              {sections.faq.items?.map((item, index) => (
                <Card key={index} className="border border-gray-200 overflow-hidden">
                  <button
                    className="w-full p-6 text-left flex justify-between items-center hover:bg-gray-50 transition"
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  >
                    <span className="font-semibold text-gray-900 pr-4">{item.question}</span>
                    {openFaq === index ? (
                      <ChevronUp className="w-5 h-5 text-gray-500 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-500 flex-shrink-0" />
                    )}
                  </button>
                  {openFaq === index && (
                    <div className="px-6 pb-6 text-gray-600">
                      {item.answer}
                    </div>
                  )}
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      {sections.cta?.enabled && (
        <section className="py-16 md:py-24 bg-gradient-to-br from-blue-600 to-green-500">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">{sections.cta.title}</h2>
            <p className="text-lg text-white/90 mb-8">{sections.cta.subtitle}</p>
            <Button 
              size="lg" 
              onClick={handleSignup}
              className="bg-white text-blue-600 hover:bg-gray-100 text-lg px-8 py-6 rounded-xl shadow-lg"
            >
              {sections.cta.buttonText} <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </section>
      )}

      {/* Footer */}
      {sections.footer?.enabled && (
        <footer className="bg-gray-900 text-white py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-4 gap-8">
              <div className="col-span-2">
                <div className="flex items-center space-x-2 mb-4">
                  <img src="/logo.png" alt="NotiMax" className="w-10 h-10 rounded-xl" />
                  <span className="text-xl font-bold">{sections.footer.companyName}</span>
                </div>
                <p className="text-gray-400 mb-4">{sections.footer.description}</p>
                {sections.footer.email && (
                  <p className="text-gray-400">
                    <a href={`mailto:${sections.footer.email}`} className="hover:text-white transition">
                      {sections.footer.email}
                    </a>
                  </p>
                )}
              </div>
              <div>
                <h4 className="font-semibold mb-4">Enlaces</h4>
                <ul className="space-y-2 text-gray-400">
                  <li><a href="#features" className="hover:text-white transition">Características</a></li>
                  <li><a href="#pricing" className="hover:text-white transition">Precios</a></li>
                  <li><a href="#faq" className="hover:text-white transition">FAQ</a></li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold mb-4">Legal</h4>
                <ul className="space-y-2 text-gray-400">
                  {sections.footer.links?.map((link, index) => (
                    <li key={index}>
                      <a href={link.url} className="hover:text-white transition">{link.text}</a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
              <p>© {new Date().getFullYear()} {sections.footer.companyName}. Todos los derechos reservados.</p>
              <div className="flex justify-center gap-4 mt-2">
                <a href="/privacy" className="text-gray-500 hover:text-white transition text-sm">Política de Privacidad</a>
                <span className="text-gray-700">|</span>
                <a href="/delete-account" className="text-gray-500 hover:text-white transition text-sm">Eliminar Cuenta</a>
              </div>
            </div>
          </div>
        </footer>
      )}
    </div>
  );
};

export default LandingPage;
